/**
 * AiCloud Setup - Hybrid Model (Free Tier + User Cloud)
 *
 * User can choose:
 * 1. Free Tier: Shared Cloudflare (100 tasks/month, 10k tokens/month, 1GB storage)
 * 2. User Cloud: Own Cloudflare/Azure/GCP/AWS (unlimited, requires setup)
 */

import axios from 'axios'
import { Check, Cloud, Shield } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8888'

interface TierOption {
  id: string
  name: string
  description: string
  price: string
  limits: {
    tasks: string
    tokens: string
    storage: string
    agents: string
  }
  features: string[]
  recommended?: boolean
}

const TIER_OPTIONS: TierOption[] = [
  {
    id: 'free',
    name: 'Free Tier',
    description: 'Perfect for trying out AiCloud',
    price: '$0/month',
    limits: {
      tasks: '100 tasks/month',
      tokens: '10k LLM tokens/month',
      storage: '1GB storage',
      agents: '1 agent'
    },
    features: [
      'Shared Cloudflare infrastructure',
      'Workers + D1 + R2',
      'Community support',
      'Web UI access'
    ]
  },
  {
    id: 'pro',
    name: 'Pro (User Cloud)',
    description: 'Your own cloud, your rules',
    price: '$10 FLZ/month',
    limits: {
      tasks: '1000 tasks/month',
      tokens: '100k LLM tokens/month',
      storage: '10GB storage',
      agents: '10 agents'
    },
    features: [
      'Your own cloud account',
      'Choose: Cloudflare, Azure, GCP, AWS',
      'Full data ownership',
      'Email support',
      'Python CLI access'
    ],
    recommended: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise (User Cloud)',
    description: 'Unlimited power for teams',
    price: '$50+ FLZ/month',
    limits: {
      tasks: 'Unlimited',
      tokens: 'Unlimited',
      storage: 'Unlimited',
      agents: 'Unlimited'
    },
    features: [
      'Your own cloud account',
      'Multi-cloud support',
      'Priority support',
      'Custom features',
      'SLA guarantees',
      'Team collaboration'
    ]
  }
]

const PROVIDERS = [
  {
    id: 'cloudflare',
    name: 'Cloudflare',
    logo: '☁️',
    description: 'Fastest deployment (~2 min)',
    pricing: 'Workers $5/mo + $0.50/M requests',
    features: ['Workers', 'D1', 'R2', 'Workers AI', 'KV']
  },
  {
    id: 'azure',
    name: 'Azure',
    logo: '🔷',
    description: 'Enterprise-grade',
    pricing: 'Functions $0.20/M executions',
    features: ['Functions', 'Cosmos DB', 'Blob', 'OpenAI', 'Redis']
  },
  {
    id: 'gcp',
    name: 'Google Cloud',
    logo: '🔴',
    description: 'AI-native platform',
    pricing: 'Functions $0.40/M invocations',
    features: ['Cloud Functions', 'Firestore', 'Storage', 'Vertex AI', 'Memorystore']
  },
  {
    id: 'aws',
    name: 'AWS',
    logo: '🟠',
    description: 'Most mature ecosystem',
    pricing: 'Lambda $0.20/M requests',
    features: ['Lambda', 'DynamoDB', 'S3', 'Bedrock', 'ElastiCache']
  }
]

export default function AiCloudSetup() {
  const navigate = useNavigate()
  const [selectedTier, setSelectedTier] = useState<string | null>(null)
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleFreeTierSignup = async () => {
    setLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(
        `${API_URL}/api/aicloud/free/signup`,
        { accept_terms: true },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      if (response.data.success) {
        // Navigate to aicloud page
        navigate('/aicloud')
      }
    } catch (err) {
      setError((err as any).response?.data?.detail as string | null || 'Failed to sign up for free tier')
    } finally {
      setLoading(false)
    }
  }

  const handleUserCloudSetup = () => {
    if (!selectedProvider) {
      setError('Please select a cloud provider')
      return
    }

    // Navigate to provider-specific setup page
    navigate(`/aicloud/setup/${selectedProvider}`)
  }

  return (
    <div style={{ padding: '40px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '60px' }}>
        <h1 style={{ fontSize: '48px', marginBottom: '20px' }}>
          <Cloud size={48} style={{ verticalAlign: 'middle', marginRight: '16px' }} />
          AiCloud Setup
        </h1>
        <p style={{ fontSize: '20px', color: 'var(--text-secondary)' }}>
          Choose your infrastructure: Shared cloud (easy) or your own cloud (private)
        </p>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          padding: '16px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          marginBottom: '32px',
          color: '#ef4444'
        }}>
          {error}
        </div>
      )}

      {/* Step 1: Choose Tier */}
      <div style={{ marginBottom: '60px' }}>
        <h2 style={{ fontSize: '32px', marginBottom: '32px' }}>
          Step 1: Choose Your Tier
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '24px'
        }}>
          {TIER_OPTIONS.map((tier) => (
            <div
              key={tier.id}
              onClick={() => setSelectedTier(tier.id)}
              style={{
                padding: '32px',
                background: selectedTier === tier.id ? 'rgba(59, 130, 246, 0.1)' : 'var(--card-bg)',
                border: selectedTier === tier.id ? '2px solid #3b82f6' : '1px solid var(--border-color)',
                borderRadius: '12px',
                cursor: 'pointer',
                position: 'relative',
                transition: 'all 0.2s'
              }}
            >
              {tier.recommended && (
                <div style={{
                  position: 'absolute',
                  top: '-12px',
                  right: '20px',
                  padding: '4px 12px',
                  background: '#3b82f6',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>
                  RECOMMENDED
                </div>
              )}

              <h3 style={{ fontSize: '24px', marginBottom: '8px' }}>{tier.name}</h3>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                {tier.description}
              </p>

              <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '24px' }}>
                {tier.price}
              </div>

              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  {tier.limits.tasks}
                </div>
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  {tier.limits.tokens}
                </div>
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  {tier.limits.storage}
                </div>
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                  {tier.limits.agents}
                </div>
              </div>

              <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '16px' }}>
                {tier.features.map((feature, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <Check size={16} style={{ color: '#10b981', marginRight: '8px' }} />
                    <span style={{ fontSize: '14px' }}>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Step 2: Choose Provider (if Pro/Enterprise) */}
      {selectedTier && selectedTier !== 'free' && (
        <div style={{ marginBottom: '60px' }}>
          <h2 style={{ fontSize: '32px', marginBottom: '32px' }}>
            Step 2: Choose Your Cloud Provider
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '24px'
          }}>
            {PROVIDERS.map((provider) => (
              <div
                key={provider.id}
                onClick={() => setSelectedProvider(provider.id)}
                style={{
                  padding: '24px',
                  background: selectedProvider === provider.id ? 'rgba(59, 130, 246, 0.1)' : 'var(--card-bg)',
                  border: selectedProvider === provider.id ? '2px solid #3b82f6' : '1px solid var(--border-color)',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>{provider.logo}</div>
                <h3 style={{ fontSize: '20px', marginBottom: '8px' }}>{provider.name}</h3>
                <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                  {provider.description}
                </p>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                  {provider.pricing}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {provider.features.map((feature, idx) => (
                    <span
                      key={idx}
                      style={{
                        padding: '4px 8px',
                        background: 'rgba(59, 130, 246, 0.1)',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Button */}
      <div style={{ textAlign: 'center' }}>
        {selectedTier === 'free' && (
          <button
            onClick={() => { void handleFreeTierSignup() }}
            disabled={loading}
            style={{
              padding: '16px 48px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Setting up...' : 'Start Free Tier'}
          </button>
        )}

        {selectedTier && selectedTier !== 'free' && (
          <button
            onClick={handleUserCloudSetup}
            disabled={!selectedProvider || loading}
            style={{
              padding: '16px 48px',
              background: selectedProvider ? '#3b82f6' : '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: selectedProvider && !loading ? 'pointer' : 'not-allowed',
              opacity: selectedProvider && !loading ? 1 : 0.6
            }}
          >
            Continue to {selectedProvider ? PROVIDERS.find(p => p.id === selectedProvider)?.name : 'Provider'} Setup
          </button>
        )}
      </div>

      {/* Info Box */}
      <div style={{
        marginTop: '60px',
        padding: '24px',
        background: 'rgba(59, 130, 246, 0.05)',
        border: '1px solid rgba(59, 130, 246, 0.2)',
        borderRadius: '12px'
      }}>
        <h3 style={{ fontSize: '20px', marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
          <Shield size={24} style={{ marginRight: '12px' }} />
          Zero-Knowledge Architecture
        </h3>
        <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
          With User Cloud (Pro/Enterprise), ALL your data stays in YOUR cloud account.
          RunAI never sees your data, only orchestrates deployment and monitoring.
          You can export and migrate anytime with zero vendor lock-in.
        </p>
      </div>
    </div>
  )
}
